import { type ReactNode } from "react";

import { useHistory } from "../hooks/useHistory";
import {
  ROTULO_FRESCURA,
  dataHora,
  duracao,
  frescura,
  gigabytes,
  haQuantoTempo,
  horaComSegundos,
  num,
} from "../lib/format";
import { alturaDoPonto, estadoMar } from "../lib/ondulacao";
import type { Piloto } from "../lib/pilotos";
import { densificar, escalar } from "../lib/series";
import { rotuloFuso } from "../lib/tempo";
import type { NodeState } from "../types";
import { Autonomia } from "./Autonomia";
import { GraficoBanda, GraficoLinhas, type SerieBoia } from "./charts/Graficos";
import { Clips } from "./Clips";
import { Disponibilidade } from "./Disponibilidade";
import { EstadoMar } from "./EstadoMar";
import { NodeChips } from "./NodeChips";
import { RangePicker, type Intervalo } from "./RangePicker";

/** Aparece quando o cliente parou de seguir o cursor. Nunca se corta em silêncio. */
const NOTA_TRUNCADO =
  "Intervalo grande: mostradas as primeiras 5000 leituras. Escolhe uma janela menor para ver o resto.";

interface Props {
  no: NodeState;
  /** Todas as boias, para as fichas de comparação. */
  nos: NodeState[];
  /** Outras boias a desenhar nos gráficos, além de `no`. */
  comparadas: string[];
  aoAlternarComparada: (code: string) => void;
  intervalo: Intervalo;
  aoMudarIntervalo: (i: Intervalo) => void;
  aoFechar: () => void;
  /** Modo piloto: a janela é a do piloto e as secções "ao vivo" saem. */
  piloto?: Piloto | null;
}

export function NodePanel({
  no,
  nos,
  comparadas,
  aoAlternarComparada,
  intervalo,
  aoMudarIntervalo,
  aoFechar,
  piloto = null,
}: Props): ReactNode {
  // A boia do painel vai sempre à frente: é dela que se pedem as quatro
  // grandezas, e é a dela que a secção "Agora" mostra.
  const codigos = [no.code, ...comparadas.filter((c) => c !== no.code)];
  const historico = useHistory(codigos, intervalo.de, intervalo.ate);
  const estado = frescura(no);
  // Todos os gráficos partilham o eixo x do filtro.
  const janela: [number, number] = [intervalo.de.getTime(), intervalo.ate.getTime()];

  const principal = historico.por[no.code];

  // A ficha desta boia no piloto ativo. `null` fora do modo piloto — e também
  // para uma boia que não fez parte do elenco (via `?node=` no URL).
  const boiaPiloto = piloto?.boias.find((b) => b.code === no.code) ?? null;

  // No modo piloto, os valores "no fim" vêm do próprio histórico já pedido: o
  // último ponto de cada série é literalmente a última coisa que a boia disse.
  const fimTemperatura = (() => {
    const pontos = principal?.temperatura?.points ?? [];
    for (let i = pontos.length - 1; i >= 0; i--) {
      const media = pontos[i]?.mean;
      if (media != null) return { valor: media, quando: pontos[i]!.ts };
    }
    return null;
  })();
  const fimStatus =
    (principal?.status.length ?? 0) > 0
      ? principal!.status[principal!.status.length - 1]!
      : null;

  // Só as boias do elenco entram na comparação: as outras não têm dados na
  // janela, e uma ficha que só desenha vazio parecia avariada.
  const nosFichas =
    piloto === null ? nos : nos.filter((n) => piloto.boias.some((b) => b.code === n.code));

  // O mar "agora" vem do último bloco imu sozinho — a mediana das últimas
  // janelas é para o histórico; a ficha do topo mostra o instante.
  const marAgora = estadoMar(no.imu === null ? [] : [no.imu]);
  const pontosImu = principal?.imu ?? [];
  const algumaEstimada = pontosImu.some((p) => p.wave_height_m == null);

  /** As séries de uma grandeza, uma por boia, na ordem dos códigos. */
  function porBoia(metrica: "temperatura" | "sonar"): SerieBoia[] {
    return codigos.flatMap((code) => {
      const serie = historico.por[code]?.[metrica];
      return serie ? [{ code, pontos: densificar(serie) }] : [];
    });
  }

  return (
    <aside className="painel" aria-label={`Boia ${no.code}`}>
      <header className="painel-topo">
        <div className="linha">
          <h2>
            {no.code} · {no.name}
          </h2>
          <button type="button" className="fechar" onClick={aoFechar} aria-label="Fechar painel">
            ×
          </button>
        </div>
        <div className="linha" style={{ marginTop: 6 }}>
          {piloto === null ? (
            <span className="distintivo">
              <i className={`ponto ${estado}`} aria-hidden="true" />
              {ROTULO_FRESCURA[estado]} — {haQuantoTempo(no.last_contact)}
            </span>
          ) : boiaPiloto !== null ? (
            <span className="distintivo">
              <i className="ponto critica" aria-hidden="true" />
              último lote às {horaComSegundos(boiaPiloto.fim)}
            </span>
          ) : (
            <span className="distintivo">fora do elenco deste piloto</span>
          )}
        </div>
        <div className="papel" style={{ marginTop: 6 }}>
          {no.role === "central" ? "Nó central" : "Nó standard"} · {no.camera_model}
        </div>
        {/* Uma vez, à cabeça, a cobrir as métricas e os quatro gráficos. Sem
            isto ninguém sabe se o que está no ecrã é o que está na base. */}
        <div className="papel" style={{ marginTop: 2 }}>
          Instantes em {rotuloFuso(intervalo.ate)}
        </div>
      </header>

      {piloto !== null && (
        <div className="seccao">
          <h3>Piloto</h3>
          {boiaPiloto === null ? (
            <p className="nota" style={{ margin: 0 }}>
              Esta boia não entrou na água viva neste piloto — os gráficos abaixo
              mostram o que existir na janela, que pode ser nada.
            </p>
          ) : (
            <>
              <div className="metricas">
                <div className="metrica">
                  <span className="rotulo">Primeiro lote</span>
                  <div className="valor" style={{ fontSize: 15 }}>
                    {horaComSegundos(boiaPiloto.inicio)}
                  </div>
                </div>
                <div className="metrica">
                  <span className="rotulo">Entrada na água</span>
                  <div className="valor" style={{ fontSize: 15 }}>
                    {boiaPiloto.imersao === null ? "—" : horaComSegundos(boiaPiloto.imersao)}
                  </div>
                </div>
                <div className="metrica">
                  <span className="rotulo">Último lote</span>
                  <div className="valor" style={{ fontSize: 15 }}>
                    {horaComSegundos(boiaPiloto.fim)}
                  </div>
                </div>
                <div className="metrica">
                  <span className="rotulo">Sobreviveu na água</span>
                  <div className="valor" style={{ fontSize: 15 }}>
                    {boiaPiloto.imersao === null
                      ? "—"
                      : duracao(
                          (Date.parse(boiaPiloto.fim) - Date.parse(boiaPiloto.imersao)) / 1000,
                        )}
                  </div>
                </div>
                <Metrica
                  rotulo="Temperatura no fim"
                  valor={fimTemperatura?.valor ?? null}
                  unidade="°C"
                  casas={2}
                  quando={fimTemperatura?.quando ?? null}
                />
                <Metrica
                  rotulo="Tensão no fim"
                  valor={fimStatus?.voltage_v ?? null}
                  unidade="V"
                  casas={2}
                  quando={fimStatus?.timestamp ?? null}
                />
              </div>
              <p className="nota" style={{ marginTop: 8, marginBottom: 0 }}>
                {boiaPiloto.gnss
                  ? "Posições no mapa: fixes GNSS reais até ao último lote."
                  : "Sem um único fix GNSS no piloto — no mapa está a posição planeada, tracejada."}
                {" "}Depois do último lote a boia nunca mais contactou o servidor.
              </p>
            </>
          )}
        </div>
      )}

      {piloto === null && (
      <>
      <div className="seccao">
        <h3>Agora</h3>
        <div className="metricas">
          <Metrica
            rotulo="Temperatura"
            valor={no.temperature?.temperature_c ?? null}
            unidade="°C"
            casas={2}
            quando={no.temperature?.timestamp ?? null}
          />
          <Metrica
            rotulo="Distância ao leito"
            valor={no.sonar?.distance_mm == null ? null : no.sonar.distance_mm / 10}
            unidade="cm"
            casas={0}
            quando={no.sonar?.timestamp ?? null}
            // Sem eco não é avaria: o alcance é 6 m e a maré em Viana varia
            // 0,3–3,8 m. Dizê-lo por extenso evita a leitura de "sensor partido".
            vazio="sem eco"
          />
          <Metrica
            rotulo="Bateria"
            valor={no.status?.battery_pct ?? null}
            unidade="%"
            casas={0}
            quando={no.status?.timestamp ?? null}
            // A tensão não serve de indicador: a curva do LiFePO4 é plana
            // entre ~20–90%. Isto vem do contador de coulomb do nó.
          />
          <Metrica
            rotulo="Tensão"
            valor={no.status?.voltage_v ?? null}
            unidade="V"
            casas={2}
            quando={no.status?.timestamp ?? null}
          />
          <Metrica
            rotulo="Corrente"
            valor={no.status?.current_ma ?? null}
            unidade="mA"
            casas={0}
            quando={no.status?.timestamp ?? null}
          />
          {/* Pitch e roll saíram daqui: quem olha para a ficha quer saber
              como está o mar, não a atitude da placa. Os números técnicos
              continuam na API e no gráfico de alturas. */}
          <div className="metrica">
            <span className="rotulo">Estado do mar</span>
            {marAgora.rotulo === null ? (
              <div className="vazio">sem dados</div>
            ) : (
              <div className="valor" style={{ fontSize: 15 }}>
                {marAgora.rotulo}
              </div>
            )}
            {no.imu !== null && <div className="quando">{horaComSegundos(no.imu.window_end)}</div>}
          </div>
          <Metrica
            rotulo="Altura das ondas"
            valor={marAgora.alturaM}
            unidade="m"
            casas={2}
            quando={no.imu?.window_end ?? null}
          />
        </div>
      </div>

      <div className="seccao">
        <h3>Posição</h3>
        <div className="metricas">
          <div className="metrica">
            <span className="rotulo">Latitude</span>
            <div className="valor" style={{ fontSize: 15 }}>
              {no.latitude}
            </div>
          </div>
          <div className="metrica">
            <span className="rotulo">Longitude</span>
            <div className="valor" style={{ fontSize: 15 }}>
              {no.longitude}
            </div>
          </div>
        </div>
        <p className="nota" style={{ marginTop: 8, marginBottom: 0 }}>
          {no.position_source === "gnss"
            ? `Fix GNSS de ${haQuantoTempo(no.position?.timestamp ?? null)}.`
            : "Sem fix GNSS — desenhada na posição planeada."}
        </p>
        {no.status !== null && (
          <p className="nota" style={{ marginTop: 4, marginBottom: 0 }}>
            Disco livre {gigabytes(no.status.disk_free_space)} GB · a funcionar há{" "}
            {duracao(no.status.uptime)} · CPU {num(no.status.cpu_temperature, 1)} °C
          </p>
        )}
      </div>
      </>
      )}

      {/* Uma linha de filtro só, acima de tudo o que ela abrange. */}
      <div className="seccao">
        <h3>Histórico</h3>
        {piloto === null ? (
          <RangePicker valor={intervalo} aoMudar={aoMudarIntervalo} />
        ) : (
          // A janela de um piloto é um facto histórico, não um filtro: não se
          // estica nem desliza. Escreve-se, em vez de se dar um seletor.
          <p className="nota" style={{ margin: 0 }}>
            Janela fixa do piloto — {dataHora(piloto.de)} a {dataHora(piloto.ate)},{" "}
            {rotuloFuso(intervalo.ate)}.
          </p>
        )}
        <NodeChips
          nos={nosFichas}
          principal={no.code}
          comparadas={comparadas}
          aoAlternar={aoAlternarComparada}
        />
        {historico.erro !== null && (
          <p className="aviso-erro" style={{ marginTop: 10 }}>
            {historico.erro}
          </p>
        )}
      </div>

      <div className={historico.aRecarregar ? "a-recarregar" : undefined}>
        <Disponibilidade
          linhas={codigos.map((code) => ({
            code,
            temperatura: historico.por[code]?.temperatura ?? null,
          }))}
        />

        {/* Folgas fixas por grandeza: sem elas o eixo automático esticava
            variações de centésimos até parecerem drama. */}
        <GraficoBanda titulo="Temperatura" unidade="°C" casas={2} folga={2} boias={porBoia("temperatura")} janela={janela} />

        <GraficoBanda
          titulo="Distância ao leito"
          unidade="cm"
          casas={0}
          folga={20}
          boias={porBoia("sonar").map((b) => ({ ...b, pontos: escalar(b.pontos, 0.1) }))}
          janela={janela}
          notaVazios={(vazios, total) =>
            `${vazios} de ${total} intervalos sem eco — leito fora do alcance de 6 m, ou sem lote recebido.`
          }
        />

        {/* Estado do mar e bateria são só da boia do painel: são grandezas
            operacionais de cada nó, não gradientes entre nós. */}
        <EstadoMar imu={pontosImu} truncado={principal?.truncado === true} />

        <GraficoLinhas
          titulo="Altura das ondas"
          unidade="m"
          casas={2}
          dados={pontosImu.map((p) => ({
            t: Date.parse(p.window_end),
            altura: alturaDoPonto(p).m,
            swell: p.swell_height_m ?? null,
          }))}
          series={[
            { chave: "altura", rotulo: "significativa", slot: 1 },
            { chave: "swell", rotulo: "swell (>8 s)", slot: 2 },
          ]}
          folga={0.3}
          piso={0}
          janela={janela}
          boia={no.code}
          nota={
            [
              algumaEstimada
                ? "Pontos sem estatística de ondas do nó: altura estimada do movimento vertical, com período assumido de 8 s."
                : null,
              principal?.truncado === true ? NOTA_TRUNCADO : null,
            ]
              .filter((n) => n !== null)
              .join(" ") || undefined
          }
        />

        <Autonomia status={principal?.status ?? []} truncado={principal?.truncado === true} />

        {/* A tensão fica na dica de "agora": a curva plana do LiFePO4 fazia
            do gráfico de tensão uma linha reta sem informação de carga. */}
        <GraficoLinhas
          titulo="Bateria"
          unidade="%"
          casas={0}
          dados={(principal?.status ?? []).map((p) => ({
            t: Date.parse(p.timestamp),
            battery_pct: p.battery_pct,
          }))}
          series={[{ chave: "battery_pct", rotulo: "bateria", slot: 1 }]}
          dominio={[0, 100]}
          janela={janela}
          boia={no.code}
          nota={principal?.truncado === true ? NOTA_TRUNCADO : undefined}
        />
      </div>

      {/* Só da boia do painel, como a ondulação: vídeo compara-se mal entre
          nós, e cada clip são ~15 MB — não se pedem seis de cada vez. */}
      <Clips code={no.code} de={intervalo.de} ate={intervalo.ate} />
    </aside>
  );
}

interface MetricaProps {
  rotulo: string;
  valor: number | null;
  unidade: string;
  casas: number;
  quando: string | null;
  vazio?: string;
}

function Metrica({ rotulo, valor, unidade, casas, quando, vazio }: MetricaProps): ReactNode {
  return (
    <div className="metrica">
      <span className="rotulo">{rotulo}</span>
      {valor === null ? (
        <div className="vazio">{vazio ?? "sem dados"}</div>
      ) : (
        <div className="valor">
          {num(valor, casas)}
          <span className="unidade">{unidade}</span>
        </div>
      )}
      {/* Cada valor com o seu instante: `status` e `imu` viajam ~1 vez por
          minuto, portanto são legitimamente mais velhos que a temperatura. */}
      {quando !== null && <div className="quando">{horaComSegundos(quando)}</div>}
    </div>
  );
}
